import { computed, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { CriterioTipoDado } from '@shared/criterio';
import { MongoQuery } from '@ucast/mongo';
import { DateTime } from 'luxon';
import { DataManagerProviderFiltroConfig } from '../contracts/data-manager-provider';
import { DataManagerColuna, DataManagerColunaSimples } from '../contracts/data-manager.model';

export type DataManagerFiltroRange = {
  min: number | DateTime;
  max: number | DateTime;
};

type FiltroConfig = DataManagerProviderFiltroConfig & {
  id: string;
};

/** Representa o filtro como um todo */
export class DataManagerFiltros implements Iterable<DataManagerFiltro> {
  private _filtros = new Map<string, DataManagerFiltro>();
  private _filtrosPronto = signal(false);
  private colunasFiltro: FiltroConfig[] | null = null;
  private _limiteLinhas = Number.POSITIVE_INFINITY;
  private _primeiraSelecao = true;

  get hasValor() {
    for (const filtro of this._filtros.values()) {
      if (filtro.hasValor) {
        return true;
      }
    }
    return false;
  }

  get invalid() {
    for (const filtro of this._filtros.values()) {
      if (filtro.invalid) {
        return true;
      }
    }
    return false;
  }

  get limiteLinhas() {
    return this._limiteLinhas;
  }

  get primeiraSelecao() {
    return this._primeiraSelecao;
  }

  limpar() {
    this._filtros.forEach((filtro) => filtro.reset());
  }

  /** Reseta status de alteração do filtro, mantendo o valor atual */
  resetAlteracoes(manterValor: boolean) {
    this._primeiraSelecao = false;
    this._filtros.forEach((filtro) => filtro.resetAlteracoes(manterValor));
  }

  setColunas(colunas: DataManagerColuna[]) {
    if (!this.colunasFiltro) {
      return;
    }

    for (const colunaFiltro of this.colunasFiltro) {
      const coluna = colunas.find((c) => c.id === colunaFiltro.id);
      if (coluna) {
        this._filtros.set(colunaFiltro.id, new DataManagerFiltro(coluna, colunaFiltro));
      }
    }
    this._filtrosPronto.set(true);
  }

  setColunasFiltro(colunasFiltro: FiltroConfig[]) {
    this.colunasFiltro = colunasFiltro;
  }

  setFiltroConfig(config: { limiteLinhas: number }) {
    this._limiteLinhas = config.limiteLinhas | Number.POSITIVE_INFINITY;
  }

  setFiltroOptions(id: string, valores: Record<string, any>[], atributoLookup?: string) {
    const filtro = this._filtros.get(id);
    if (filtro) {
      filtro.setOptions(valores, atributoLookup);
    }
  }

  get filtrosPronto() {
    return this._filtrosPronto;
  }

  get filtroString() {
    const query: MongoQuery[] = [];
    for (const filtro of this._filtros.values()) {
      const item = filtro.filtroItem;
      if (item) {
        query.push(item);
      }
    }
    return query.length > 0 ? JSON.stringify({ $and: query }) : null;
  }

  [Symbol.iterator](): Iterator<DataManagerFiltro, any, undefined> {
    return this._filtros.values();
  }
}

/** Representa um item do filtro */
export class DataManagerFiltro {
  private isLookup = false;
  private lookupToId = new Map<string, string>();
  private _componente: 'range' | 'date' | 'select' | 'input' | 'dateRange' | 'selectUnique';
  private _options = signal<{ valor: string; descricao: string }[]>([]);
  private _valor!: FormControl | FormGroup;
  private _valorOriginal!: any;

  options = computed(() => {
    const listaOpcoesSignal = this._filtroConfig.listaOpcoes;
    if (listaOpcoesSignal) {
      const listaOpcoes = listaOpcoesSignal();
      if (listaOpcoes && listaOpcoes.length > 0) {
        return listaOpcoes;
      }
    }

    return this._options();
  });

  constructor(
    private _coluna: DataManagerColuna,
    private _filtroConfig: FiltroConfig,
  ) {
    this._componente = this.getComponente();

    const validators = this.buildValidators();

    switch (this._componente) {
      case 'dateRange':
        this._valor = new FormGroup({
          min: new FormControl(null, validators),
          max: new FormControl(null, validators),
        });
        break;

      case 'range':
        this._valor = new FormGroup({
          min: new FormControl(null, validators),
          max: new FormControl(null, validators),
        });
        break;

      case 'select':
        this._valor = new FormControl([], validators);
        break;

      case 'selectUnique':
        this._valor = new FormControl([], validators);
        break;

      default:
        this._valor = new FormControl(null, validators);
        break;
    }
  }

  get componente() {
    return this._componente;
  }

  get dirty() {
    return this._valor.dirty;
  }

  get errors() {
    return this._valor.errors;
  }

  get hasErrors() {
    if (['dateRange', 'range'].includes(this.componente)) {
      const group = this._valor as FormGroup;
      return (
        (group.controls['min']?.errors && Object.keys(group.controls['min']!.errors!).length > 0) ||
        (group.controls['max']?.errors && Object.keys(group.controls['max']!.errors!).length > 0)
      );
    }
    return this._valor.errors && Object.keys(this._valor.errors).length > 0;
  }

  get invalid() {
    return this._valor.invalid;
  }

  get semEntradaManual() {
    return this._filtroConfig.semEntradaManual || false;
  }

  get hasValor() {
    const valor = this._valor.value;
    if (Array.isArray(valor)) {
      return valor.length > 0;
    }

    if (valor?.min !== undefined) {
      return !!(valor.min || valor.max);
    }

    return !!valor;
  }

  get label() {
    return this._coluna.descricao;
  }

  get idFiltro() {
    return this._coluna.id;
  }

  get listaValores() {
    const listaValoresChip = this._filtroConfig.listaValoresChip;
    if (listaValoresChip) {
      return listaValoresChip(this._valor.value);
    }

    const valor = this._valor.value;

    if (Array.isArray(valor)) {
      return valor.join(', ');
    }

    if (valor) {
      return valor;
    }

    return null;
  }

  get valorAsControl() {
    return this._valor as FormControl;
  }

  get valorAsGroup() {
    return this._valor as FormGroup;
  }

  get filtroItem(): MongoQuery | null {
    return this.getFiltroItem();
  }

  reset() {
    switch (this._componente) {
      case 'dateRange':
      case 'range':
        this._valor.reset({
          min: undefined,
          max: undefined,
        });
        break;
      case 'select':
        this._valor.reset([]);
        break;
      case 'selectUnique':
        this._valor.reset([]);
        break;
      default:
        this._valor.reset();
    }
  }

  /** Reseta alterações para o valor atual */
  resetAlteracoes(manterValor: boolean) {
    if (manterValor) {
      this._valorOriginal = this._valor.value;
      this._valor.reset(this._valor.value);
    } else if (this._valorOriginal) {
      this._valor.reset(this._valorOriginal);
    }
  }

  setOptions(valores: Record<string, any>[], atributoLookup?: string) {
    this.isLookup = !!atributoLookup;
    const dadosAdicionais = this._filtroConfig.dadosAdicionais;
    const idDescricao = atributoLookup || 'id';

    if (this.isLookup) {
      this.lookupToId.clear();
    }

    const options = valores
      .reduce<{ valor: string; descricao: string }[]>((prev, curr) => {
        if (this.isLookup) {
          this.lookupToId.set(curr[atributoLookup!], curr['id']);
        }

        prev!.push({
          valor: atributoLookup ? curr[atributoLookup] : curr['id'],
          descricao: dadosAdicionais?.reduce((prevda, currda) => {
            return prevda + ` - ${curr[currda.id]}`;
          }, curr[idDescricao]),
        });

        return prev;
      }, [])
      .sort((a, b) => a.descricao.localeCompare(b.descricao));

    this._options.set(options);
  }

  private buildValidators() {
    const validators = [] as ValidatorFn[];

    if (this._filtroConfig.obrigatorio) {
      validators.push(Validators.required);
    }

    if (this._filtroConfig.range) {
      validators.push(this.rangeValidator());
    }

    return validators;
  }

  private getComponente() {
    if (this._filtroConfig.range) {
      return this._coluna.tipoDado === CriterioTipoDado.data ? 'dateRange' : 'range';
    }

    if (this._filtroConfig.listaOpcoes || (this._coluna as DataManagerColunaSimples).lookup) {
      return this._filtroConfig.unique ? 'selectUnique' : 'select';
    }

    return this._coluna.tipoDado === CriterioTipoDado.data
      ? 'date'
      : this._coluna.tipoDado === CriterioTipoDado.valor
        ? 'range'
        : 'input';
  }

  private getFiltroItem(): MongoQuery | null {
    if (this._filtroConfig.parser) {
      const valor = this._valor.value;
      if (valor) {
        const filtroParsed = this._filtroConfig.parser(valor);
        return filtroParsed;
      }
      return null;
    }

    const nomeCampo = this._coluna.id;
    switch (this._componente) {
      case 'select':
        const valorSelect = this.getValorFinalFiltro();
        if (Array.isArray(valorSelect) && valorSelect.length > 0) {
          return {
            [nomeCampo]: {
              $in: valorSelect.filter((item) => item).map((item) => item || ''),
            },
          };
        }
        break;
      case 'selectUnique':
        const valorSelectUnique = this.getValorFinalFiltro();
        if (Array.isArray(valorSelectUnique) && valorSelectUnique.length > 0) {
          return {
            [nomeCampo]: {
              $in: valorSelectUnique.filter((item) => item).map((item) => item || ''),
            },
          };
        }
        break;
      case 'input':
        const valorInput = this._valor.value;
        if (valorInput && typeof valorInput === 'string' && valorInput.length > 0) {
          const queryOr = (valorInput as string).split(',').map((item) => {
            const range = item.split('-');
            if (range.length > 1) {
              if (this._coluna.tipoDado === CriterioTipoDado.numerico) {
                return {
                  [nomeCampo]: {
                    $gte: +range[0].trim(),
                    $lte: +range[1].trim(),
                  },
                };
              }
              return {
                [nomeCampo]: { $gte: range[0].trim(), $lte: range[1].trim() },
              };
            }
            if (this._coluna.tipoDado === CriterioTipoDado.numerico) {
              return { [nomeCampo]: { $eq: +item.trim() } };
            }
            return { [nomeCampo]: { $eq: item.trim() } };
          });
          return { $or: queryOr };
        }
        break;
      case 'range':
        const valorRange = this._valor.value;
        if (valorRange && (valorRange.min !== null || valorRange.max !== null)) {
          const queryRange: MongoQuery = {};
          if (valorRange.min !== null) {
            queryRange['$gte'] = valorRange.min;
          }
          if (valorRange.max !== null) {
            queryRange['$lte'] = valorRange.max;
          }
          return {
            [nomeCampo]: queryRange,
          };
        }
        break;
      case 'dateRange':
        const dateRange = this._valor.value;
        if (dateRange && (dateRange.min || dateRange.max)) {
          if (this._coluna.tipoDado === CriterioTipoDado.alfanumerico) {
            return {
              [nomeCampo]: {
                $gte: (dateRange.min as DateTime)?.toISODate() || undefined,
                $lte: dateRange.max?.endOf('day').toISODate() || undefined,
              },
            };
          } else if (this._coluna.tipoDado === CriterioTipoDado.data) {
            return {
              [nomeCampo]: {
                $gte: dateRange.min?.toJSDate() || undefined,
                $lte: dateRange.max?.endOf('day').toJSDate() || undefined,
              },
            };
          }
        }
        break;
      case 'date':
        const valorDateMax = this._valor.value;

        if (valorDateMax) {
          if (valorDateMax instanceof DateTime) {
            return {
              [nomeCampo]: {
                $lte: valorDateMax.endOf('day').toJSDate() || undefined,
              },
            };
          }
        }
        break;
    }

    return null;
  }

  private getValorFinalFiltro() {
    if (this.isLookup) {
      const valor = this._valor.value;
      if (Array.isArray(valor)) {
        return valor.map((v) => this.lookupToId.get(v));
      }

      return this.lookupToId.get(valor);
    }

    return this._valor.value;
  }

  private rangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as DataManagerFiltroRange;
      if (!value) {
        return null;
      }

      if (value.min !== undefined && value.max !== undefined && value.min > value.max) {
        return { rangeInvalido: true };
      }

      return null;
    };
  }
}
